import i18n, { changeLanguage } from 'i18next';
import { pick } from 'lodash';
import { combineReducers } from 'redux';
import { call, put, select, takeLatest } from 'redux-saga/effects';

import { environment } from 'environments/environment';
import { ExtendedAxiosResponse } from 'helpers/apiClient';
import {
  createActionType,
  createApiActionCreators,
  createReducer,
  RequestActionTypes,
} from 'helpers/redux/reduxHelpers';
import { AppState } from 'helpers/store/models/AppState';
import { ApiClientRequest } from 'models/ApiClientRequest';
import { LocaleTypeMap, UserPreferences } from 'models/context/UserPreferences';
import { getApiUrl, performApiCall } from 'saga/apiClient/ducks';
import { AuthActionTypes } from 'saga/auth/ducks';
import { api as sharedObjectsApi } from 'saga/sharedObjects/api';
import { i18NextInitialized } from 'saga/translation/ducks';

/* STATE */
export interface ContextUserPreferencesState {
  preferences: UserPreferences | null;
}

/* ACTION TYPES */
export enum ContextUserPreferencesActionTypes {
  SetContextUserPreferences = '@@Context/UserPreferences/SET_CONTEXT_USER_PREFERENCES',
}

/* ACTIONS */
export const setContextUserPreferencesActions = createApiActionCreators(
  ContextUserPreferencesActionTypes.SetContextUserPreferences
);

/* REDUCERS */
const initialState: ContextUserPreferencesState = {
  preferences: {
    darkMode: false,
    vatPrice: true,
    locale: LocaleTypeMap[environment.appTranslation.defaultLocale],
    refreshPage: false,
  },
};

const preferences = createReducer(initialState.preferences, {
  [ContextUserPreferencesActionTypes.SetContextUserPreferences]: {
    [RequestActionTypes.SUCCESS]: (_state: UserPreferences | null, payload: UserPreferences) => {
      return {
        ..._state,
        ...pick(payload, ['darkMode', 'locale', 'vatPrice', 'refreshPage']),
      };
    },
    [RequestActionTypes.FAILURE]: () => null,
  },
  [AuthActionTypes.Logout]: {
    [RequestActionTypes.SUCCESS]: () => null,
  },
});

export default combineReducers<ContextUserPreferencesState>({ preferences });

/* SELECTORS */
const selectContextUserPreferences = (state: AppState) => state.context.userPreferences || {};

export const selectContextUserPreferencesInfo = (state: AppState): UserPreferences | null =>
  selectContextUserPreferences(state).preferences;

/* SAGAS */
function* setPreferences({ payload: userPreferencesData }: any) {
  if (!userPreferencesData.skipSharedObjectsSession) {
    const request: ApiClientRequest = {
      apiCall: call(
        sharedObjectsApi.setPreferences,
        yield getApiUrl(environment.urlConfig.sharedObjects),
        userPreferencesData
      ),
    };
    const resp: ExtendedAxiosResponse = yield call(performApiCall, request);

    if (!resp.ok) {
      console.log('setPreferences failure: setPreferences failed');
      yield put(setContextUserPreferencesActions.failure());

      return;
    }
  }

  if (!userPreferencesData.refreshPage) {
    const locale = Object.keys(LocaleTypeMap).find((key) => LocaleTypeMap[key] === userPreferencesData.locale);
    const i18Initialized: boolean = yield select(i18NextInitialized);

    if (i18Initialized && locale !== i18n?.language) {
      changeLanguage(locale);
    }
  }

  yield put(setContextUserPreferencesActions.success(userPreferencesData));

  if (userPreferencesData.refreshPage) {
    window.location.reload();
  }
}

/* EXPORT */
export function* contextUserPreferencesSaga() {
  yield takeLatest(
    createActionType(ContextUserPreferencesActionTypes.SetContextUserPreferences, RequestActionTypes.REQUEST),
    setPreferences
  );
}
